import * as React from "react";
import { ReactNode } from "react";

export interface Point {
	x: number;
	y: number;
}

export interface Props {
	/** The current position of the thing being dragged at the start of a drag.  If not set, subject is the position in the containing element */
	subject?: () => Point;
	/** Called with the new position at the start of a drag */
	start?: (pos: Point) => void;
	/** Called with the new position during a drag */
	drag?: (pos: Point) => void;
	/** Called with the new position at the end of a drag */
	end?: (pos: Point) => void;
	/** If set, keyboard arrow keys (not WASD) will also call the dragging operations */
	keyEmulation?: number;
	/** If set, do NOT connect drag handlers; only key emulation */
	noDrag?: boolean;
	/** If set, drag only when this callback returns true */
	filter?: () => boolean;
	children?: ReactNode;
}

function pointFromMouse(event: MouseEvent): Point {
	return { x: event.pageX, y: event.pageY };
}
function pointFromTouch(event: TouchEvent): Point | undefined {
	return event.touches[0] && { x: event.touches[0].pageX, y: event.touches[0].pageY };
}

export default class D3Drag extends React.PureComponent<Props> {
	private ref = React.createRef<HTMLElement>();
	private childRef?: React.RefObject<HTMLElement> = undefined;

	private currentElement() {
		return (this.childRef || this.ref).current!;
	}

	private currentOrigin?: Point = undefined;
	private currentDrag?: Point = undefined;

	private down(p: Point) {
		if (this.props.filter && !this.props.filter()) {
			return false;
		}

		// TODO: does subject + contain make sense?
		let subject: Point;
		if (this.props.subject) {
			subject = this.props.subject();
		} else {
			const box = this.currentElement().getBoundingClientRect();
			subject = { x: p.x - box.left, y: p.y - box.top };
		}
		this.currentOrigin = { x: subject.x - p.x, y: subject.y - p.y };
		this.currentDrag = subject;
		if (this.props.start) {
			this.props.start(subject);
		}
		return true;
	}

	private drag(p: Point) {
		if (this.currentOrigin) {
			if (this.props.drag) {
				this.props.drag(
					(this.currentDrag = {
						x: this.currentOrigin.x + p.x,
						y: this.currentOrigin.y + p.y,
					})
				);
			}
			return true;
		} else {
			return false;
		}
	}

	private up(p?: Point) {
		if (this.currentOrigin) {
			if (this.props.end) {
				if (p) {
					this.props.end({
						x: this.currentOrigin.x + p.x,
						y: this.currentOrigin.y + p.y,
					});
				} else {
					this.props.end(this.currentDrag!);
				}
			}
			this.currentOrigin = undefined;
			this.currentDrag = undefined;
			return true;
		} else {
			return false;
		}
	}

	private mouseDown = (event: MouseEvent) => {
		if (event.button) {
			// don't drag on right clicks
			return;
		}
		if (this.down(pointFromMouse(event))) {
			event.stopPropagation(); // TODO: might need stopImmediatePropogation?
			event.preventDefault();
		}
	};

	private mouseMove = (event: MouseEvent) => {
		this.drag(pointFromMouse(event));
	};

	private mouseUp = (event: MouseEvent) => {
		this.up(pointFromMouse(event));
	};

	private touchStart = (event: TouchEvent) => {
		if (this.down(pointFromTouch(event)!)) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	private touchMove = (event: TouchEvent) => {
		if (this.drag(pointFromTouch(event)!)) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	private touchEnd = (event: TouchEvent) => {
		if (this.up(pointFromTouch(event))) {
			event.stopPropagation();
		}
	};

	private touchCancel = (event: TouchEvent) => {
		if (this.up(pointFromTouch(event))) {
			event.stopPropagation();
		}
	};

	private handleKey = (event: React.KeyboardEvent) => {
		const p = (this.props.subject && this.props.subject()) || { x: 0, y: 0 };
		const q = { x: p.x, y: p.y };
		const i = this.props.keyEmulation || 1;
		switch (event.keyCode) {
			case 37:
				q.x -= i;
				break;
			case 38:
				q.y -= i;
				break;
			case 39:
				q.x += i;
				break;
			case 40:
				q.y += i;
				break;
			default:
				return;
		}
		if (this.props.start) {
			this.props.start(p);
		}
		if (this.props.drag) {
			this.props.drag(q);
		}
		if (this.props.end) {
			this.props.end(q);
		}
		event.preventDefault();
	};

	componentDidMount() {
		const e = this.currentElement();
		addEventListener("mousemove", this.mouseMove, { passive: true });
		addEventListener("mouseup", this.mouseUp, { passive: true });
		if (!this.props.noDrag) {
			e.addEventListener("mousedown", this.mouseDown);
			e.addEventListener("touchstart", this.touchStart);
			e.addEventListener("touchmove", this.touchMove);
			e.addEventListener("touchend", this.touchEnd);
			e.addEventListener("touchcancel", this.touchCancel);
		}
	}

	componentWillUnmount() {
		const e = this.currentElement();
		removeEventListener("mousemove", this.mouseMove);
		removeEventListener("mouseup", this.mouseUp);
		if (!this.props.noDrag) {
			e.removeEventListener("mousedown", this.mouseDown);
			e.removeEventListener("touchstart", this.touchStart);
			e.removeEventListener("touchmove", this.touchMove);
			e.removeEventListener("touchend", this.touchEnd);
			e.removeEventListener("touchcancel", this.touchCancel);
		}
	}

	render() {
		const elt = React.Children.only(this.props.children);
		if (!React.isValidElement(elt) || typeof elt.type !== "string") {
			throw new Error("Can only enhance element child!");
		}
		const childRef = (elt as React.ClassAttributes<HTMLElement>).ref || undefined;
		if (childRef != null && typeof childRef !== "object") {
			throw new Error("Child ref, if provided, must be a RefObject");
		}
		this.childRef = childRef as React.RefObject<HTMLElement>;

		const props: React.DOMAttributes<HTMLElement> & React.ClassAttributes<HTMLElement> = {
			ref: childRef || this.ref,
		};
		if (this.props.keyEmulation) {
			props.onKeyDown = this.handleKey;
		}
		return React.cloneElement(elt, props);
	}
}
